// What the firmware-update banner should say, as a pure function (ADR 0202;
// core ADR-164) — the firmware twin of updateBannerState.ts, carrying the
// same hard-won rule:
//
// A 202 FROM THE INSTALL ROUTE IS NOT EVIDENCE. Every line comes from what
// the BOAT reported, and a stored outcome counts as THIS attempt's only when
// its `commandKey` matches the `idempotencyKey` the 202 handed back.
//
// What differs from the core twin is honesty about time and consequence: a
// firmware install downloads hundreds of megabytes over a boat link, writes a
// whole rootfs slot, and ends in a full gateway reboot — so the waiting copy
// says so, the wait horizon covers the gateway's full allowed lifecycle, and
// a `failed` may be the bootloader having rolled the gateway back.

import {
  FIRMWARE_OWNER_WAIT_LIMIT_MS,
  isFirmwareAcceptanceActive,
  type FirmwareUpdateAvailable,
} from '@neomaris/protocol';

import { isCoreOutdated } from './coreVersion';
import type { FirmwareUpdateAttempt } from './firmwareUpdateAttempt';
import type { Boat } from './useBoats';

/** Re-exported for the view's tests and countdown consumers. */
export const FIRMWARE_WAIT_LIMIT_MS = FIRMWARE_OWNER_WAIT_LIMIT_MS;

/** Same page-reload rule as the core banner: recent outcomes only, as history. */
export const FIRMWARE_RECENT_OUTCOME_MS = 60 * 60_000;

export type FirmwareStatusTone = 'waiting' | 'failed';

type FirmwareBlock = NonNullable<Boat['firmware']>;

export interface FirmwareBannerInput {
  /** The boat's reported running firmware version (null = unknown). */
  firmwareVersion: string | null;
  /** Exact verified artifact this gateway can install, from the boats payload. */
  firmwareUpdate: FirmwareUpdateAvailable | null;
  lastUpdate: FirmwareBlock['lastUpdate'];
  /** The persisted key the 202 returned; correlation, never outcome evidence. */
  attempt: FirmwareUpdateAttempt | null;
  /** The POST itself is in flight. */
  sending: boolean;
  /** The POST failed — the boat never heard anything. */
  error: string | null;
  /** The firmware version whose offer was closed on this browser. */
  dismissedVersion: string | null;
  now: number;
}

export interface FirmwareBannerView {
  visible: boolean;
  offer: string | null;
  status: { text: string; tone: FirmwareStatusTone } | null;
  action: { label: string; disabled: boolean } | null;
  dismissible: boolean;
  settled: boolean;
  timedOut: boolean;
  waiting: boolean;
}

const NOTHING: FirmwareBannerView = {
  visible: false,
  offer: null,
  status: null,
  action: null,
  dismissible: false,
  settled: false,
  timedOut: false,
  waiting: false,
};

function isTerminal(phase: string): boolean {
  return phase === 'rejected' || phase === 'failed' || phase === 'complete';
}

/** Compact binary file size for the consent line. */
export function formatFirmwareSize(bytes: number): string {
  const units = ['B', 'KB', 'MB', 'GB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 || value >= 10 ? Math.round(value) : Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

export function firmwareBannerView(input: FirmwareBannerInput): FirmwareBannerView {
  const { firmwareVersion, firmwareUpdate, lastUpdate, attempt, sending, error, now } = input;
  const { dismissedVersion } = input;
  const firmwareLatest = firmwareUpdate?.version ?? null;
  const reportedAccepted = lastUpdate?.phase === 'accepted';
  const statusVersion =
    (reportedAccepted ? lastUpdate.version : null) ??
    attempt?.targetVersion ??
    firmwareLatest ??
    lastUpdate?.version ??
    null;
  // The strict-semver compare is version-scheme-generic; NeOS versions are
  // X.Y.Z exactly like core's.
  const outdated = firmwareLatest !== null && isCoreOutdated(firmwareVersion, firmwareLatest);
  const mine =
    attempt !== null && lastUpdate !== null && lastUpdate.commandKey === attempt.commandKey;
  const settled = mine && isTerminal(lastUpdate.phase);
  const reportedForTarget =
    lastUpdate !== null && statusVersion !== null && lastUpdate.version === statusVersion;
  const reportedAcceptedActive = isFirmwareAcceptanceActive(lastUpdate?.phase, lastUpdate?.at, now);
  const reportedAcceptedTimedOut = reportedAccepted && !reportedAcceptedActive;
  const localTimedOut =
    attempt !== null &&
    !settled &&
    !reportedAcceptedActive &&
    now - attempt.startedAt > FIRMWARE_WAIT_LIMIT_MS;
  const timedOut = reportedAcceptedTimedOut || localTimedOut;

  // A complete beat for this target is global boat truth, so reloads and a
  // second owner's browser do not resurrect an already-finished offer while
  // the identity heartbeat catches up.
  if (reportedForTarget && lastUpdate?.phase === 'complete') {
    return { ...NOTHING, settled: mine };
  }
  if (!outdated && attempt === null && error === null && !reportedAccepted) return NOTHING;

  let status: { text: string; tone: FirmwareStatusTone } | null = null;
  if (error !== null) {
    status = { text: error, tone: 'failed' };
  } else if (reportedAcceptedActive) {
    status = {
      text: `Updating firmware to NeOS ${statusVersion ?? ''} — the gateway downloads the bundle, then reboots on its own. This can take a while on a slow link.`,
      tone: 'waiting',
    };
  } else if (mine && lastUpdate !== null && isTerminal(lastUpdate.phase)) {
    status = {
      text: lastUpdate.detail
        ? `The firmware update didn't complete: ${lastUpdate.detail}`
        : "The firmware update didn't complete — the gateway is back on its previous firmware.",
      tone: 'failed',
    };
  } else if (timedOut) {
    status = {
      text: 'No answer from the gateway. If it lost power mid-update it comes back on its previous firmware by itself — try again when it is back online.',
      tone: 'failed',
    };
  } else if (sending || (attempt !== null && !settled)) {
    status = { text: 'Sent to your boat — waiting for it to answer…', tone: 'waiting' };
  } else if (
    lastUpdate !== null &&
    lastUpdate.phase !== 'complete' &&
    isTerminal(lastUpdate.phase) &&
    lastUpdate.at !== null
  ) {
    const at = new Date(lastUpdate.at).getTime();
    if (Number.isFinite(at) && now - at < FIRMWARE_RECENT_OUTCOME_MS && now - at >= 0) {
      status = {
        text: lastUpdate.detail
          ? `The last firmware attempt didn't complete: ${lastUpdate.detail}`
          : "The last firmware attempt didn't complete.",
        tone: 'failed',
      };
    }
  }

  // Only a BARE offer can be dismissed, same reasoning as the core banner.
  // Firmware has no enforcement, so a dismissal is always honored while it
  // still names the latest release.
  const closed =
    status === null && dismissedVersion !== null && dismissedVersion === firmwareLatest;
  if (closed) return NOTHING;

  const waiting =
    error === null &&
    (sending || reportedAcceptedActive || (attempt !== null && !settled && !timedOut));

  return {
    visible: outdated || status !== null,
    dismissible: outdated && status === null,
    offer: outdated
      ? `Firmware update available — NeOS ${firmwareLatest ?? ''} · ${formatFirmwareSize(firmwareUpdate?.size ?? 0)} download`
      : null,
    status,
    action: outdated
      ? {
          label: reportedAcceptedActive
            ? 'Update in progress'
            : waiting
              ? 'Approving…'
              : 'Review firmware update',
          disabled: waiting,
        }
      : null,
    settled,
    timedOut,
    waiting,
  };
}
